/**
 * ARCA voucher type codes (tabla de tipos de comprobante, as used by WSFE and Mis Comprobantes).
 * Only the types an accounting studio loads for VAT books are listed; an unknown code is a
 * validation error, never a guess.
 */
export const VOUCHER_TYPES = [
    { code: 1, name: "Factura A", letter: "A", kind: "factura" },
    { code: 2, name: "Nota de Débito A", letter: "A", kind: "nota_debito" },
    { code: 3, name: "Nota de Crédito A", letter: "A", kind: "nota_credito" },
    { code: 4, name: "Recibo A", letter: "A", kind: "recibo" },
    { code: 6, name: "Factura B", letter: "B", kind: "factura" },
    { code: 7, name: "Nota de Débito B", letter: "B", kind: "nota_debito" },
    { code: 8, name: "Nota de Crédito B", letter: "B", kind: "nota_credito" },
    { code: 9, name: "Recibo B", letter: "B", kind: "recibo" },
    { code: 11, name: "Factura C", letter: "C", kind: "factura" },
    { code: 12, name: "Nota de Débito C", letter: "C", kind: "nota_debito" },
    { code: 13, name: "Nota de Crédito C", letter: "C", kind: "nota_credito" },
    { code: 15, name: "Recibo C", letter: "C", kind: "recibo" },
    { code: 51, name: "Factura M", letter: "M", kind: "factura" },
    { code: 52, name: "Nota de Débito M", letter: "M", kind: "nota_debito" },
    { code: 53, name: "Nota de Crédito M", letter: "M", kind: "nota_credito" },
    { code: 54, name: "Recibo M", letter: "M", kind: "recibo" },
    // Tabla de comprobantes de ARCA (servicioscf.afip.gob.ar/publico/comprobantes/cae.aspx).
    { code: 58, name: "Cuenta de Venta y Líquido Producto M", letter: "M", kind: "liquidacion" },
    { code: 59, name: "Liquidación M", letter: "M", kind: "liquidacion" },
    { code: 60, name: "Cuenta de Venta y Líquido Producto A", letter: "A", kind: "liquidacion" },
    { code: 61, name: "Cuenta de Venta y Líquido Producto B", letter: "B", kind: "liquidacion" },
    { code: 63, name: "Liquidación A", letter: "A", kind: "liquidacion" },
    { code: 64, name: "Liquidación B", letter: "B", kind: "liquidacion" },
    { code: 81, name: "Tique Factura A", letter: "A", kind: "tique" },
    { code: 82, name: "Tique Factura B", letter: "B", kind: "tique" },
    { code: 83, name: "Tique", letter: "-", kind: "tique" },
    { code: 201, name: "Factura de Crédito Electrónica MiPyME A", letter: "A", kind: "factura" },
    { code: 202, name: "Nota de Débito Electrónica MiPyME A", letter: "A", kind: "nota_debito" },
    { code: 203, name: "Nota de Crédito Electrónica MiPyME A", letter: "A", kind: "nota_credito" },
    { code: 206, name: "Factura de Crédito Electrónica MiPyME B", letter: "B", kind: "factura" },
    { code: 207, name: "Nota de Débito Electrónica MiPyME B", letter: "B", kind: "nota_debito" },
    { code: 208, name: "Nota de Crédito Electrónica MiPyME B", letter: "B", kind: "nota_credito" },
    { code: 211, name: "Factura de Crédito Electrónica MiPyME C", letter: "C", kind: "factura" },
    { code: 212, name: "Nota de Débito Electrónica MiPyME C", letter: "C", kind: "nota_debito" },
    { code: 213, name: "Nota de Crédito Electrónica MiPyME C", letter: "C", kind: "nota_credito" },
];
const BY_CODE = new Map(VOUCHER_TYPES.map((type) => [type.code, type]));
export function voucherType(code) {
    return BY_CODE.get(code);
}
/** Credit notes reduce débito and crédito fiscal; every other type adds. */
export function voucherSign(code) {
    return voucherType(code)?.kind === "nota_credito" ? -1 : 1;
}
export const COMPROBANTE_CLASSES = [
    { id: "F", label: "Factura", codes: { A: 1, B: 6, C: 11, M: 51 } },
    { id: "ND", label: "Nota de débito", codes: { A: 2, B: 7, C: 12, M: 52 } },
    { id: "NC", label: "Nota de crédito", codes: { A: 3, B: 8, C: 13, M: 53 } },
    { id: "R", label: "Recibo", codes: { A: 4, B: 9, C: 15, M: 54 } },
    { id: "L", label: "Cuenta de venta y líquido producto", codes: { A: 60, B: 61, M: 58 } },
    { id: "LQ", label: "Liquidación", codes: { A: 63, B: 64, M: 59 } },
    { id: "T", label: "Tique factura", codes: { A: 81, B: 82 } },
    { id: "TQ", label: "Tique", codes: { "-": 83 } },
    { id: "FCE", label: "Factura de crédito MiPyME", codes: { A: 201, B: 206, C: 211 } },
    { id: "NDE", label: "Nota de débito MiPyME", codes: { A: 202, B: 207, C: 212 } },
    { id: "NCE", label: "Nota de crédito MiPyME", codes: { A: 203, B: 208, C: 213 } },
];
const CLASS_BY_ID = new Map(COMPROBANTE_CLASSES.map((c) => [c.id, c]));
/** The ARCA code of a comprobante class and letter, or undefined when that pair does not exist. */
export function comprobanteCode(classId, letter) {
    return CLASS_BY_ID.get(classId)?.codes[letter];
}
/** The class and letter of an ARCA code; every code in `VOUCHER_TYPES` has exactly one. */
export function comprobanteOf(code) {
    for (const c of COMPROBANTE_CLASSES) {
        for (const [letter, value] of Object.entries(c.codes)) {
            if (value === code)
                return { classId: c.id, letter: letter };
        }
    }
    return undefined;
}
//# sourceMappingURL=voucher-types.js.map